import { Checkbox, FormControlLabel } from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { useActionRequest } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { Callout, CalloutVariant } from "../../../../Callout";
import { SUBMIT_FIELD_NAMES } from "../../../DataRequestSubjectCard";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { AlertCard } from "@truevault/aquilo";

export const CorrectDataStep = () => {
  const [org] = usePrimaryOrganization();

  const { request, refreshRequest, setStepCompleted } = useDataRequestProcessingContext();

  const api = useCallback(
    ({ request, correctionFinished, nothingToCorrect }) => {
      const existingValues = Object.fromEntries(
        Object.entries(request).filter(([k]) => SUBMIT_FIELD_NAMES.includes(k)),
      );
      const values = Object.assign({}, existingValues, {
        correctionFinished: correctionFinished ?? request.correctionFinished,
        nothingToCorrect: nothingToCorrect ?? request.nothingToCorrect,
      }) as any;
      return Api.dataRequest.updateRequest(org.id, request.id, values);
    },
    [request, org.id],
  );

  const { fetch: updateField } = useActionRequest({ api });

  const handleCorrectionFinishedUpdated = async (checked: boolean) => {
    await updateField({
      request,
      correctionFinished: checked,
      nothingToCorrect: checked ? false : request.nothingToCorrect,
    });
    refreshRequest();
  };

  const handleNothingToCorrectUpdated = async (checked: boolean) => {
    await updateField({
      request,
      nothingToCorrect: checked,
      correctionFinished: checked ? false : request.correctionFinished,
    });
    refreshRequest();
  };

  const isStepComplete =
    (request.correctionFinished ?? false) || (request.nothingToCorrect ?? false);

  useEffect(() => {
    setStepCompleted(isStepComplete);
  }, [request.correctionFinished, request.nothingToCorrect, setStepCompleted]);

  return (
    <>
      <div className="text-t3 mb-std">
        The consumer’s rectification message is provided below. Correct the consumer’s personal data
        in your records. If you have questions about the consumer’s message, or if you do not
        believe the requested correction should be made, email the consumer from your privacy email
        to request clarification. When you’ve made the necessary corrections, check the box below
        and proceed to the next step.
      </div>

      <div className="tw-boundary mb-std">
        <AlertCard variant="info" containerClasses="mt-0 mb-0">
          <div className="text-t3">
            If any of the personal data that the consumer wants corrected came from a third-party
            source and you can't make the correction yourself, inform the consumer of the name of
            that third party.
          </div>
        </AlertCard>
      </div>

      <Callout
        variant={CalloutVariant.Clear}
        title={`The consumer provided the following details with their ${request.dataRequestType.label}`}
        className="mb-md"
      >
        {request.description}
      </Callout>

      <div className="flex flex-col gap-2">
        <FormControlLabel
          control={
            <Checkbox
              checked={request.correctionFinished ?? false}
              onChange={({ target }) => handleCorrectionFinishedUpdated(target.checked)}
              disabled={request.nothingToCorrect ?? false}
            />
          }
          label="We've corrected any inaccurate data about this consumer"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={request.nothingToCorrect ?? false}
              onChange={({ target }) => handleNothingToCorrectUpdated(target.checked)}
              disabled={request.correctionFinished ?? false}
            />
          }
          label="We've determined that no data needs to be corrected"
        />
      </div>
    </>
  );
};
